import "./viewAvailableTutors.scss";
import Layout from "components/CsmComponents/Layouts/Layout";
import CustomSelect from "components/Select/Select";
import Button from "components/Button/Button";
import AddIconCircle from "assets/imgs/icons/addRounded.svg";
import { useState, useMemo, Fragment } from "react";
import Table from "components/Table/Table";
import AvatarSm from "assets/imgs/icons/AvatarOrangeSM.svg";
import { Modal } from "react-bootstrap";
import OutlinedInput from "components/Input/OutlinedInput";
import OutlinedSelect from "components/Select/OutlinedSelect";
import CalendarIcon from "assets/imgs/icons/calendarOutlinedRed.svg";
import { DatePicker, Loader } from "rsuite";
import { useLocation } from "react-router-dom";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useContext } from "react";
import { AuthContext } from "context/AuthContext";
import listStudents from "service/user/csm/student/list-students";
import getCourses from "service/courses/get-courses";
import freeSlots from "service/user/csm/sessions/get-free-slots";
import moment from "moment";
import scheduleSession from "service/user/csm/sessions/schedule-session";
import toast from "react-hot-toast";
import Header from "components/CsmComponents/Header/Header";

function ViewAvailableTutors() {
  const [show, setShow] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const queryClient = useQueryClient();
  const { state } = useLocation();

  const [data, setData] = useState({
    teacherId: 0,
    selectedTime: "",
    selectedDay: "",
    selectedSlotId: 0,
    tutor: "",
    classStartDate: "",
    classStartDateIST: "",
    slots: [],
    students: {
      options: [],
      selected: {
        label: state?.student || "",
        value: state?.studentId || "",
      },
    },
    courses: {
      options: [],
      selected: {
        label: state?.course || "",
        value: state?.courseId || "",
      },
      selectedInModal: {
        label: state?.course || "",
        value: state?.courseId || "",
      },
      selectedCFSInModal: {
        label: state?.course || "",
        value: state?.courseId || "",
      },
    },
    timezones: {
      options: [
        {
          label: "Asia/Calcutta",
          value: "IST",
        },
        {
          label: "Student's Time Zone",
          value: "ST",
        },
      ],
      selected: {
        label: "Asia/Calcutta",
        value: "IST",
      },
    },
    bookingStatus: "confirmed",
  });
  const [warning, setWarning] = useState({
    course: "",
    courseForSalary: "",
    date: "",
  });
  const { authState } = useContext(AuthContext);

  const dayRender = (slotId, day, startTime, teacherId, teacherName) => (
    <div
      onClick={() => {
        setData((prevState) => {
          return {
            ...prevState,
            teacherId,
            selectedTime: startTime,
            selectedDay: day,
            selectedSlotId: slotId,
            tutor: teacherName,
          };
        });
        setShow(true);
      }}
      className="d-flex align-items-center justify-content-center gap-1 text-decoration-underline"
    >
      {startTime}
    </div>
  );

  const queries = useQueries({
    queries: [
      {
        queryKey: [
          "slots",
          authState?.userInfo?.cifNumber,
          data.courses.selected.value,
          data.students.selected.value,
          data.timezones.selected.value,
        ],

        queryFn: () =>
          freeSlots({
            csmId: authState?.userInfo?.cifNumber,
            params: {
              courseId: data.courses.selected.value,
              studentId: data.students.selected.value,
              timeZoneType: data.timezones.selected.value,
            },
          }),

        enabled: !!(
          authState?.userInfo?.cifNumber &&
          data.courses.selected.value &&
          data.students.selected.value
        ),
        onSuccess: (slots = []) => {
          const rows = [];
          for (let i = 0; i < slots.length; i++) {
            const { teacherId, teacherName, freeSlots = [] } = slots[i];
            const currentRow = [
              {
                teacherId,
                tutor: teacherName,
              },
            ];
            let alreadyAddedDays = [];
            let round = 0;
            for (let j = 0; j < freeSlots.length; j++) {
              const { dayOfTheWeek, startTime, slotId } = freeSlots[j];
              const day = dayOfTheWeek.slice(0, 3).toLowerCase();
              let dayFound = false;

              if (alreadyAddedDays.includes(day)) {
                round++;
                currentRow.push({
                  [day]: dayRender(
                    slotId,
                    day,
                    startTime,
                    teacherId,
                    teacherName
                  ),
                });
                dayFound = true;
                alreadyAddedDays = [];
              }

              if (!dayFound) {
                currentRow[round][day] = dayRender(
                  slotId,
                  day,
                  startTime,
                  teacherId,
                  teacherName
                );
              }

              alreadyAddedDays.push(day);
            }

            rows.push(...currentRow);
          }

          setData((prevState) => {
            return {
              ...prevState,
              slots: rows,
            };
          });
        },
      },
      {
        queryKey: ["students", authState?.userInfo?.cifNumber],
        queryFn: () => listStudents(authState?.userInfo?.cifNumber),
        enabled: !!authState?.userInfo?.cifNumber,

        onSuccess: (students = []) =>
          setData((prevState) => {
            return {
              ...prevState,

              students: {
                ...prevState.students,

                options: students.map(({ studentId, studentName }) => {
                  return {
                    label: studentName,
                    value: studentId,
                  };
                }),
              },
            };
          }),
      },
      {
        queryKey: "courses",
        queryFn: getCourses,

        onSuccess: (courses = []) =>
          setData((prevState) => {
            return {
              ...prevState,

              courses: {
                ...prevState.courses,

                options: courses.map(({ courseId, courseName }) => {
                  return {
                    label: courseName,
                    value: courseId,
                  };
                }),
              },
            };
          }),
      },
    ],
  });
  const { mutateAsync, isLoading } = useMutation(scheduleSession, {
    onSuccess: (d) => {
      queryClient.invalidateQueries(["slots"]);
      // console.log(d);
    },
    onError: (e) => toast.error(e?.response?.data?.message),
  });

  const columns = useMemo(
    () => [
      {
        Header: "Tutor",
        accessor: "tutor",
      },
      {
        Header: "Mon",
        accessor: "mon",
      },
      {
        Header: "Tue",
        accessor: "tue",
      },
      {
        Header: "Wed",
        accessor: "wed",
      },
      {
        Header: "Thu",
        accessor: "thu",
      },
      {
        Header: "Fri",
        accessor: "fri",
      },
      {
        Header: "Sat",
        accessor: "sat",
      },
      {
        Header: "Sun",
        accessor: "sun",
      },
    ],
    []
  );

  const startDateToggler = () => {
    return (
      <OutlinedInput
        appendIcon={
          <img
            src={CalendarIcon}
            alt=""
            className="csmAssignSchedule__modal_calender_icon"
          />
        }
        value={data.classStartDate}
        placeholder="Class Start Date"
        label="Class Start Date (IST)"
      />
    );
  };
  const endDateToggler = () => {
    return (
      <OutlinedInput
        appendIcon={
          <img
            src={CalendarIcon}
            alt=""
            className="csmAssignSchedule__modal_calender_icon"
          />
        }
        value={data.classStartDateIST}
        placeholder="Class End Date"
        label="Class End Date (IST) only if known"
      />
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let warning = {
      course: "",
      courseForSalary: "",
      date: "",
    };
    let flag = 0;

    if (data.courses.selectedInModal.label === "") {
      warning.course = "Course is required";
      flag = 1;
    }
    if (data.courses.selectedCFSInModal.label === "") {
      warning.course = "Course is required";
      flag = 1;
    }
    if (data.classStartDate === "") {
      warning.date = "Date is required";
      flag = 1;
    }
    setWarning(warning);
    if (flag === 1) return;

    const payload = {
      courseId: data.courses.selectedInModal.value,
      studentId: data.students.selected.value,
      teacherId: data.teacherId,
      slotId: data.selectedSlotId,
      status: data.bookingStatus === "confirmed" ? 1 : 0,
      startDate: data.classStartDate,
    };

    if (data.courses.selectedCFSInModal.label !== "") {
      payload.courseForSalaryId = data.courses.selectedCFSInModal.value;
    }
    if (data.classStartDateIST !== "") {
      payload.startDateIST = data.classStartDateIST;
    }
    if (state?.requestId) {
      payload.requestId = state?.requestId;
    }

    toast.promise(
      mutateAsync({
        csmId: authState?.userInfo?.cifNumber,
        payload,
      }).then(() => setShow(false)),
      {
        loading: <strong>Saving schedule...</strong>,
        success: <strong>Schedule Added Succesfully</strong>,
        error: <strong>Schedule Failed to Save</strong>,
      }
    );
  };
  return (
    <Layout searchVisible={true}>
      <div className="row">
        <Header />
      </div>
      <div className="row py-3 mt-4">
        {/* Assign Schedule Modal start */}
        <Modal
          dialogClassName="viewAvailableTutors__modal_width"
          show={show}
          centered
          onHide={setShow}
        >
          <form
            onSubmit={handleSubmit}
            className="csmAssignSchedule__form_wrapper py-5 px-5"
          >
            <div className="csmAssignSchedule__modal_title">
              Assign Schedule
            </div>
            <div className="d-flex align-items-center mt-2 gap-1">
              <span className="fw-500 cif__text_primary font-md">
                Time Slot Selected:
              </span>
              <span className="fw-500 cif__text_secondary font-md text-capitalize">
                {data.selectedDay} {data.selectedTime} (
                {data.timezones.selected.label})
              </span>
            </div>
            <div className="csmAssignSchedule__modal_form_wrapper mt-4">
              <div className="d-flex align-items-center justify-content-between gap-3">
                <OutlinedInput
                  placeholder="Student Name"
                  label="Student Name"
                  value={data.students.selected.label}
                  disabled
                />
                <OutlinedInput
                  placeholder="Tutor Name"
                  label="Tutor Name"
                  value={data.tutor}
                  disabled
                />
              </div>
              <div className="mt-4">
                <div className="viewAvailableTutors__radio_wrapper">
                  <label
                    className="viewAvailableTutors__book_session_label"
                    htmlFor=""
                  >
                    Booking Status
                  </label>
                  <div className="d-flex align-items-center gap-4">
                    {["confirmed", "blocked"].map((text, i) => (
                      <div key={i} className="d-flex align-items-center gap-2">
                        <input
                          type="radio"
                          className="cif__radio"
                          name="booking"
                          id={text}
                          checked={data.bookingStatus === text}
                          onChange={() =>
                            setData((prevState) => {
                              return {
                                ...prevState,
                                bookingStatus: text,
                              };
                            })
                          }
                        />
                        <label htmlFor={text} className="text-capitalize">
                          {text}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <OutlinedSelect
                  options={data.courses.options}
                  searchable={true}
                  searchIcon={true}
                  label="Course"
                  error={warning.course}
                  value={data.courses.selectedInModal}
                  onChange={(selectedInModal) =>
                    setData((prevState) => {
                      return {
                        ...prevState,
                        courses: {
                          ...prevState.courses,
                          selectedInModal,
                        },
                      };
                    })
                  }
                />
              </div>
              <div className="mt-4">
                <OutlinedSelect
                  options={data.courses.options}
                  searchable={true}
                  searchIcon={true}
                  error={warning.courseForSalary}
                  label="Course For Salary"
                  value={data.courses.selectedCFSInModal}
                  onChange={(selectedCFSInModal) =>
                    setData((prevState) => {
                      return {
                        ...prevState,
                        courses: {
                          ...prevState.courses,
                          selectedCFSInModal,
                        },
                      };
                    })
                  }
                />
              </div>
              <div className="mt-4 d-flex align-items-center justify-content-between gap-3 csmAssignSchedule__modal_date_wrapper">
                <div className="d-block w-100">
                  <DatePicker
                    preventOverflow={true}
                    toggleAs={startDateToggler}
                    oneTap={true}
                    onChange={(date) =>
                      setData((prevState) => {
                        return {
                          ...prevState,
                          classStartDate: moment(date).format("DD-MMM-yyyy"),
                        };
                      })
                    }
                  />
                  <small className="text-danger fw-600">{warning.date}</small>
                </div>
                <div className="w-100">
                  <DatePicker
                    preventOverflow={true}
                    oneTap={true}
                    toggleAs={endDateToggler}
                    onChange={(date) =>
                      setData((prevState) => {
                        return {
                          ...prevState,
                          classStartDateIST: moment(date).format("DD-MMM-yyyy"),
                        };
                      })
                    }
                  />
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-end mt-4">
                <Button
                  type="submit"
                  className="px-5 py-2"
                  rounded={true}
                  disabled={isLoading}
                >
                  {isLoading ? "Saving" : "Submit"}
                </Button>
              </div>
            </div>
          </form>
        </Modal>
        {/* Assign Schedule Modal end */}
        <div className="container-fluid container-md container-lg-fluid container-xl">
          <div className="row py-3">
            <div className="col-12">
              <h4 className="csmViewAvailableTutors__title">Find Tutor</h4>
            </div>
          </div>

          <div className="row py-3 align-items-center">
            <div className="col-12 col-sm-4 d-flex align-items-center findTutor-cs_wrapper">
              <label className="fw-500 font-md">Select Student</label>
              <CustomSelect
                errorVariant="red"
                type="underlined"
                wrapperClass=""
                value={data.students.selected}
                options={data.students.options}
                onChange={(selected) =>
                  setData((prevState) => {
                    return {
                      ...prevState,
                      students: {
                        ...prevState.students,
                        selected,
                      },
                    };
                  })
                }
              />
            </div>
            <div className="col-12 col-sm-4 d-flex align-items-center findTutor-cs_wrapper">
              <label className="fw-500 font-md">Select Course</label>
              <CustomSelect
                errorVariant="red"
                type="underlined"
                wrapperClass=""
                value={data.courses.selected}
                options={data.courses.options}
                onChange={(selected) =>
                  setData((prevState) => {
                    return {
                      ...prevState,
                      courses: {
                        ...prevState.courses,
                        selected,
                        selectedCFSInModal: selected,
                        selectedInModal: selected,
                      },
                    };
                  })
                }
              />
            </div>
            <div className="col-12 col-sm-4 d-flex align-items-center findTutor-cs_wrapper">
              <label className="fw-500 font-md">Timezone</label>
              <CustomSelect
                errorVariant="red"
                type="underlined"
                wrapperClass=""
                value={data.timezones.selected}
                options={data.timezones.options}
                onChange={(selected) =>
                  setData((prevState) => {
                    return {
                      ...prevState,
                      timezones: {
                        ...prevState.timezones,
                        selected: selected,
                      },
                    };
                  })
                }
              />
            </div>
            {/* <div className="col-12 col-sm-3 text-center text-sm-end mt-3 mt-sm-0">
              <Button
                rounded={true}
                appendIcon={
                  <img
                    style={{ width: "15px", height: "15px" }}
                    src={AddIconCircle}
                    alt="add"
                  />
                }
              >
                Submit
              </Button>
            </div> */}
          </div>
          <p className="csmViewAvailableTutors__sub_title">
            Date and time are in{" "}
            <span>{data.timezones.selected.label} Timezone</span>
          </p>

          <div className="row py-3">
            <div className="col-12 relative">
              {queries[0].isFetching && <Loader size="md" center />}
              {!queries[0].isFetching && data.slots.length === 0 && (
                <div className="csmViewAvailableTutors__data_box">
                  <div className="text-center csm_text_primary font-md fw-500">
                    There is currently no data to display.
                  </div>
                </div>
              )}
              {data.slots.length > 0 && (
                <div className="csmViewAvailableTutors__table_wrapper">
                  <Table
                    paginate={false}
                    data={data.slots}
                    columns={columns}
                    responsive={true}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
export default ViewAvailableTutors;
